import { useEffect, useState } from 'react';
import {
  CreditCard,
  Download,
  KeyRound,
  Loader2,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import {
  createSensitiveActionTicket,
  deleteAccount,
  exportAccountData,
  fetchAccount,
  updateAccountProfile,
} from '../services/api';
import { changePassword as changeSupabasePassword, requestPasswordReset } from '../services/supabase';
import { confirmDialog } from './ConfirmDialog';
import { toast } from './Toaster';

const SECTION_LABELS = {
  profile: 'Profile Settings',
  subscription: 'Subscription Plans',
  security: 'Password & Security',
  privacy: 'Privacy & Data',
};

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function AccountPanel({
  open,
  initialSection = 'profile',
  user,
  onClose,
  onOpenSettings,
  onOpenLegal,
  onProfileUpdated,
  onAccountDeleted,
}) {
  const [section, setSection] = useState(initialSection);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({ full_name: '', company_name: '' });
  const [passwords, setPasswords] = useState({ current: '', password: '', confirm: '' });
  const [sensitivePassword, setSensitivePassword] = useState('');
  const [deleteText, setDeleteText] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setSection(initialSection || 'profile');
    setError('');
    setLoading(true);
    fetchAccount()
      .then((data) => {
        setAccount(data);
        setProfile({
          full_name: data.profile?.full_name || user.name || '',
          company_name: data.profile?.company_name || user.company || '',
        });
      })
      .catch((err) => setError(err?.message || 'Could not load account settings.'))
      .finally(() => setLoading(false));
  }, [open, initialSection, user]);

  if (!open || !user) return null;

  const passwordProvider = (user.provider || 'email') === 'email';
  const sensitiveTicket = async (action) => {
    if (!passwordProvider) return '';
    if (!sensitivePassword) throw new Error('Enter your current password before exporting or deleting data.');
    const data = await createSensitiveActionTicket(sensitivePassword, action);
    return data.ticket;
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setBusy('profile');
    setError('');
    try {
      const data = await updateAccountProfile(profile);
      setAccount((current) => ({ ...current, profile: data.profile }));
      onProfileUpdated?.(data.profile);
      toast('Profile updated.', 'success');
    } catch (err) {
      setError(err?.message || 'Could not update profile.');
    } finally {
      setBusy('');
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (!passwords.current) {
      setError('Enter your current password.');
      return;
    }
    if (passwords.password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (passwords.password !== passwords.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (passwords.current === passwords.password) {
      setError('Choose a new password that is different from your current password.');
      return;
    }
    setBusy('password');
    setError('');
    try {
      await changeSupabasePassword(passwords.current, passwords.password);
      setPasswords({ current: '', password: '', confirm: '' });
      toast('Password updated.', 'success');
    } catch (err) {
      setError(err?.message || 'Could not update password.');
    } finally {
      setBusy('');
    }
  };

  const sendReset = async () => {
    setBusy('reset');
    setError('');
    try {
      await requestPasswordReset(user.email);
      toast('Password reset link sent. Check your email.', 'success');
    } catch (err) {
      setError(err?.message || 'Could not send the reset link.');
    } finally {
      setBusy('');
    }
  };

  const exportData = async () => {
    setBusy('export');
    setError('');
    try {
      const ticket = await sensitiveTicket('export');
      const data = await exportAccountData(ticket);
      setSensitivePassword('');
      downloadJson(data, `awn-adpulse-data-${new Date().toISOString().slice(0, 10)}.json`);
      toast('Your account export has been downloaded.', 'success');
    } catch (err) {
      setError(err?.message || 'Could not export account data.');
    } finally {
      setBusy('');
    }
  };

  const removeAccount = async () => {
    if (deleteText !== 'DELETE') {
      setError('Type DELETE to enable permanent deletion.');
      return;
    }
    const confirmed = await confirmDialog({
      title: 'Permanently delete this account?',
      message: 'This removes your profile, preferences, companies, uploads, campaigns, generated media, and sign-in account. It cannot be undone.',
      confirmLabel: 'Delete permanently',
      danger: true,
    });
    if (!confirmed) return;
    setBusy('delete');
    setError('');
    try {
      const ticket = await sensitiveTicket('delete');
      await deleteAccount(ticket);
      onAccountDeleted?.();
    } catch (err) {
      setError(err?.message || 'Could not delete the account.');
      setBusy('');
    }
  };

  const subscription = account?.subscription || { plan: 'free', status: 'inactive' };
  const hasPaidSubscription = subscription.plan !== 'free' && ['trialing', 'active', 'past_due'].includes(subscription.status);

  return (
    <div className="settings-overlay account-panel-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="account-panel panel glow-card" role="dialog" aria-modal="true" aria-labelledby="account-panel-title">
        <button type="button" className="settings-close" onClick={onClose} aria-label="Close account settings"><X size={18} /></button>
        <header className="account-panel-head">
          <span className="account-panel-icon" aria-hidden="true"><UserRound size={22} /></span>
          <div>
            <p className="eyebrow">Authenticated account</p>
            <h2 id="account-panel-title">{SECTION_LABELS[section]}</h2>
            <p>{user.email}</p>
          </div>
        </header>

        <div className="account-panel-layout">
          <nav className="account-panel-nav" aria-label="Account sections">
            <button type="button" className={section === 'profile' ? 'active' : ''} onClick={() => setSection('profile')}><UserRound size={16} /> Profile</button>
            <button type="button" className={section === 'subscription' ? 'active' : ''} onClick={() => setSection('subscription')}><CreditCard size={16} /> Subscription</button>
            <button type="button" className={section === 'security' ? 'active' : ''} onClick={() => setSection('security')}><KeyRound size={16} /> Password</button>
            <button type="button" className={section === 'privacy' ? 'active' : ''} onClick={() => setSection('privacy')}><ShieldCheck size={16} /> Privacy &amp; data</button>
            <button type="button" onClick={onOpenSettings}><Settings size={16} /> App preferences</button>
          </nav>

          <div className="account-panel-body">
            {loading ? (
              <div className="account-loading"><Loader2 size={20} className="spin-icon" /> Loading account…</div>
            ) : (
              <>
                {section === 'profile' && (
                  <form className="account-form" onSubmit={saveProfile}>
                    <label>Full name<input value={profile.full_name} onChange={(event) => setProfile((current) => ({ ...current, full_name: event.target.value }))} autoComplete="name" /></label>
                    <label>Company name<input value={profile.company_name} onChange={(event) => setProfile((current) => ({ ...current, company_name: event.target.value }))} autoComplete="organization" /></label>
                    <label>Email<input value={user.email} disabled readOnly /></label>
                    <button type="submit" className="primary-button" disabled={busy === 'profile'}>
                      {busy === 'profile' && <Loader2 size={16} className="spin-icon" />} Save profile
                    </button>
                  </form>
                )}

                {section === 'subscription' && (
                  <div className="account-card-stack">
                    <article className="account-summary-card">
                      <span>Current plan</span>
                      <strong>{subscription.plan === 'free' ? 'Free' : subscription.plan}</strong>
                      <small>Status: {subscription.status}</small>
                    </article>
                    <p>Paid checkout is not enabled yet. Future pricing and billing terms will be shown before purchase.</p>
                    <button type="button" className="secondary-button" disabled={!hasPaidSubscription} title={!hasPaidSubscription ? 'There is no active paid subscription to cancel.' : ''}>
                      Cancel subscription
                    </button>
                    {!hasPaidSubscription && <small>No active paid subscription is linked to this account.</small>}
                  </div>
                )}

                {section === 'security' && (
                  <div className="account-card-stack">
                    <form className="account-form" onSubmit={changePassword}>
                      <label>Current password<input type="password" value={passwords.current} onChange={(event) => setPasswords((current) => ({ ...current, current: event.target.value }))} autoComplete="current-password" required /></label>
                      <label>New password<input type="password" value={passwords.password} onChange={(event) => setPasswords((current) => ({ ...current, password: event.target.value }))} autoComplete="new-password" required /></label>
                      <label>Confirm new password<input type="password" value={passwords.confirm} onChange={(event) => setPasswords((current) => ({ ...current, confirm: event.target.value }))} autoComplete="new-password" required /></label>
                      <button type="submit" className="primary-button" disabled={busy === 'password'}>Change password</button>
                    </form>
                    <div className="account-divider"><span>or</span></div>
                    <button type="button" className="secondary-button" onClick={sendReset} disabled={busy === 'reset'}>
                      Forgot / reset password
                    </button>
                  </div>
                )}

                {section === 'privacy' && (
                  <div className="account-card-stack">
                    <div className="account-inline-links">
                      <button type="button" className="text-link" onClick={() => onOpenLegal?.('privacy')}>Privacy Policy</button>
                      <button type="button" className="text-link" onClick={() => onOpenLegal?.('cookies')}>Cookie Policy</button>
                      <button type="button" className="text-link" onClick={() => onOpenLegal?.('terms')}>Terms</button>
                    </div>
                    {passwordProvider ? (
                      <label className="account-form">
                        Current password for sensitive actions
                        <input
                          type="password"
                          value={sensitivePassword}
                          onChange={(event) => setSensitivePassword(event.target.value)}
                          autoComplete="current-password"
                        />
                      </label>
                    ) : (
                      <small>For sensitive actions, sign in again if your current login is more than 10 minutes old.</small>
                    )}
                    <article className="account-action-card">
                      <Download size={20} />
                      <div><strong>Access and export your data</strong><p>Download the account data stored for your authenticated user ID as JSON.</p></div>
                      <button type="button" className="secondary-button" onClick={exportData} disabled={busy === 'export'}>Export</button>
                    </article>
                    <article className="account-action-card account-danger-zone">
                      <Trash2 size={20} />
                      <div><strong>Delete account and data</strong><p>Type DELETE, then confirm. This action is permanent.</p></div>
                      <input value={deleteText} onChange={(event) => setDeleteText(event.target.value)} placeholder="Type DELETE" aria-label="Type DELETE to confirm" />
                      <button type="button" className="confirm-danger" onClick={removeAccount} disabled={busy === 'delete' || deleteText !== 'DELETE'}>Delete account</button>
                    </article>
                  </div>
                )}
              </>
            )}
            {error && <p className="account-error" role="alert">{error}</p>}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AccountPanel;
